import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import Pagination from '../components/Pagination';
import {
  getInventoryCategories, 
  createProduct,
  updateProduct,
  deleteProduct,
  toggleStock,
  MEDICINE_TYPES,
  bulkImportProducts,
  runBulkImportDataset,
  exportProductsAsCSV,
  getProductsPage,
  getInventoryStats,
  uploadProductImage,
  deleteProductImage,
} from '../services/inventoryApi';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const EMPTY_FORM = {
  name: '',
  brand: '',
  categoryId: '',
  price: '',
  mrp: '',
  unit: '',
  stockQuantity: '',
  prescription: false,
  emoji: '💊',
  tags: '',
  description: '',
  medicineType: 'allopathy',
};

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------
const Toast = ({ message, type, visible }) => (
  <div style={{
    position: 'fixed', bottom: 24, right: 24, zIndex: 9999,
    padding: '10px 18px', borderRadius: 10, fontSize: 13, fontWeight: 500,
    background: type === 'success' ? '#eaf3de' : type === 'error' ? '#fcebeb' : '#faeeda',
    color: type === 'success' ? '#3b6d11' : type === 'error' ? '#a32d2d' : '#854f0b',
    border: `0.5px solid ${type === 'success' ? '#97c459' : type === 'error' ? '#f09595' : '#fac775'}`,
    boxShadow: '0 4px 16px rgba(0,0,0,0.10)',
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(8px)',
    transition: 'opacity 0.2s, transform 0.2s',
    pointerEvents: 'none',
  }}>
    {message}
  </div>
);

// ---------------------------------------------------------------------------
// Confirm delete modal
// ---------------------------------------------------------------------------
const ConfirmModal = ({ product, onConfirm, onCancel, deleting }) => (
  <div className="modal-overlay" onClick={onCancel}>
    <div className="card-elevated modal-inner" onClick={e => e.stopPropagation()}
      style={{ maxWidth: 380 }}>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div style={{
          width: 52, height: 52, borderRadius: 14,
          background: '#fef2f2', border: '1px solid #fecaca',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 24, margin: '0 auto 14px',
        }}>🗑️</div>
        <h3 className="modal-title" style={{ fontSize: 17 }}>Delete product?</h3>
        <p className="modal-sub" style={{ marginTop: 6 }}>
          <strong style={{ color: '#1e1b4b' }}>{product?.name}</strong> will be permanently removed.
          This cannot be undone.
        </p>
      </div>
      <div className="modal-actions">
        <button onClick={onCancel} className="btn-outline" style={{ flex: 1, color: '#6b7280' }}>
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={deleting}
          style={{
            flex: 2, padding: '12px', fontSize: 14, fontWeight: 600,
            borderRadius: 10, cursor: deleting ? 'not-allowed' : 'pointer',
            border: 'none', background: '#dc2626', color: '#fff',
            opacity: deleting ? 0.6 : 1, transition: 'opacity .15s',
            fontFamily: 'DM Sans, sans-serif',
          }}>
          {deleting ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Bulk Import Modal
// ---------------------------------------------------------------------------

const parseCSV = (text) => {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return { headers: [], rows: [] };
  const headers = lines[0].split(',').map(h => h.replace(/^"|"$/g, '').trim().toLowerCase());
  const rows = lines.slice(1).map((line, idx) => {
    const vals = line.split(',').map(v => v.replace(/^"|"$/g, '').trim());
    const obj = {};
    headers.forEach((h, i) => { obj[h] = vals[i] ?? ''; });
    obj._rowIndex = idx + 2; // 1-based row number (including header)
    return obj;
  });
  return { headers, rows };
};

const validateRow = (row) => {
  const errs = [];
  if (!row.name?.trim()) errs.push('Name required');
  if (!row.price || isNaN(Number(row.price))) errs.push('Price must be a number');
  if (row.mrp && isNaN(Number(row.mrp))) errs.push('MRP must be a number');
  return errs;
};

const BulkImportModal = ({ onImport, onClose, token, tenantId }) => {
  const [file, setFile]         = useState(null);
  const [parsed, setParsed]     = useState(null); // { headers, rows }
  const [errors, setErrors]     = useState({});   // rowIndex → [msg]
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState('');
  const [dragOver, setDragOver] = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    setImportError('');
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const { headers, rows } = parseCSV(text);
      const rowErrors = {};
      rows.forEach(row => {
        const errs = validateRow(row);
        if (errs.length) rowErrors[row._rowIndex] = errs;
      });
      setParsed({ headers, rows });
      setErrors(rowErrors);
    };
    reader.readAsText(f);
  };

  const validRows = parsed ? parsed.rows.filter(r => !errors[r._rowIndex]) : [];
  const errorCount = Object.keys(errors).length;

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setImportError('');
    try {
      const result = await bulkImportProducts(token, tenantId, file);
      onImport(result.length);
    } catch (e) {
      setImportError(`Import failed: ${e.message}`);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="card-elevated modal-inner"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: 680, width: '95vw', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
      >
        <div className="modal-header" style={{ flexShrink: 0 }}>
          <h2 className="modal-title">📥 Bulk Import Products</h2>
          <p className="modal-sub">Upload a CSV file with columns: name, brand, price, mrp, unit, category, prescription, medicine type</p>
        </div>

        {/* Drop zone */}
        {!parsed && (
          <label
            className={`file-dropzone${dragOver ? ' drag-over' : ''}`}
            style={{ margin: '0 0 16px', display: 'block' }}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
          >
            <input
              type="file"
              accept=".csv,.txt"
              style={{ display: 'none' }}
              onChange={e => handleFile(e.target.files[0])}
            />
            <div style={{ fontSize: 36, marginBottom: 10 }}>📂</div>
            <div style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 14, color: '#4f46e5', marginBottom: 6 }}>
              Click to choose a CSV file, or drag & drop here
            </div>
            <div className="t-meta">Only .csv files supported · max 5 MB</div>
          </label>
        )}

        {/* Preview */}
        {parsed && (
          <>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12, flexShrink: 0, flexWrap: 'wrap' }}>
              <span className="t-meta">{file?.name}</span>
              <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 9999, background: '#ecfdf5', color: '#059669', border: '1px solid #6ee7b7' }}>
                ✓ {validRows.length} valid
              </span>
              {errorCount > 0 && (
                <span style={{ fontSize: 12, fontWeight: 600, padding: '2px 10px', borderRadius: 9999, background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5' }}>
                  ⚠ {errorCount} rows with errors (will be skipped)
                </span>
              )}
              <button
                className="t-link"
                style={{ marginLeft: 'auto', fontSize: 12 }}
                onClick={() => { setFile(null); setParsed(null); setErrors({}); }}
              >
                Change file
              </button>
            </div>

            <div style={{ overflow: 'auto', flex: 1, border: '1px solid #e5e7eb', borderRadius: 10 }}>
              <table className="preview-table">
                <thead>
                  <tr>
                    <th>#</th>
                    {parsed.headers.map(h => <th key={h}>{h}</th>)}
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {parsed.rows.map(row => {
                    const rowErrs = errors[row._rowIndex];
                    return (
                      <tr key={row._rowIndex} className={rowErrs ? 'row-error' : ''}>
                        <td style={{ color: '#9ca3af' }}>{row._rowIndex}</td>
                        {parsed.headers.map(h => <td key={h}>{row[h]}</td>)}
                        <td>
                          {rowErrs
                            ? <span title={rowErrs.join(', ')}>⚠ {rowErrs[0]}</span>
                            : <span style={{ color: '#059669' }}>✓</span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}

        {importError && (
          <div className="error-state" style={{ marginTop: 12, flexShrink: 0 }}><span>⚠️</span> {importError}</div>
        )}

        <div className="modal-actions" style={{ flexShrink: 0, marginTop: 16 }}>
          <button onClick={onClose} className="btn-outline" style={{ flex: 1, color: '#6b7280' }}>Cancel</button>
          <button
            className="btn-primary"
            style={{ flex: 2, opacity: (!file || importing) ? 0.6 : 1, cursor: (!file || importing) ? 'not-allowed' : 'pointer' }}
            disabled={!file || importing}
            onClick={handleImport}
          >
            {importing ? 'Importing…' : `Import CSV`}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Full Dataset Import Modal
// For the new ~124k-row pharmacy dataset (28-column format). Deliberately
// does NOT parse/preview the file client-side like BulkImportModal above —
// a 340MB CSV would freeze the browser if read into a JS string or rendered
// row-by-row in a table. Instead it just uploads the file directly (streamed
// from disk by the browser) and shows a progress bar + the server's summary.
// ---------------------------------------------------------------------------

const DatasetImportModal = ({ onImport, onClose, token, tenantId }) => {
  const [file, setFile]           = useState(null);
  const [importing, setImporting] = useState(false);
  const [progress, setProgress]   = useState(0);
  const [elapsed, setElapsed]     = useState(0);
  const [importError, setImportError] = useState('');
  const [dragOver, setDragOver]   = useState(false);

  const handleFile = (f) => {
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) {
      setImportError('Please choose a .csv file (convert the Excel export to CSV first).');
      return;
    }
    setFile(f);
    setImportError('');
  };

  // Warn before leaving the tab mid-import — closing/refreshing would silently
  // kill a multi-hundred-MB upload with no way to resume it.
  useEffect(() => {
    if (!importing) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [importing]);

  // Ticking elapsed-time counter — a static "importing…" message with no
  // movement reads as frozen once the upload itself finishes and the (silent,
  // server-side) database insert phase starts, which can take a couple of minutes.
  useEffect(() => {
    if (!importing) { setElapsed(0); return; }
    const start = Date.now();
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 1000);
    return () => clearInterval(id);
  }, [importing]);

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    setImportError('');
    setProgress(0);
    try {
      // Uploads the file (fast, tracked via onProgress), then the server
      // processes the ~125k rows in the background and this polls until
      // done — avoids holding one HTTP request open long enough for
      // CloudFront to kill it with a 504 before the import finishes.
      const result = await runBulkImportDataset(token, tenantId, file, {
        onProgress: setProgress,
      });
      onImport(result.inserted ?? 0);
    } catch (e) {
      setImportError(`Import failed: ${e.message}`);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={importing ? undefined : onClose}>
      <div
        className="card-elevated modal-inner"
        onClick={e => e.stopPropagation()}
        style={{ maxWidth: 560, width: '95vw' }}
      >
        <div className="modal-header">
          <h2 className="modal-title">🗂️ Import Full Dataset</h2>
          <p className="modal-sub">
            Upload the full 28-column dataset CSV (id, name, brand, category_id, price, mrp, unit,
            rating, review_count, in_stock, stock_quantity, prescription, emoji, description,
            medicine_type, packaging, dosage_form, composition, alcohol_interaction,
            pregnancy_interaction, expert_advice, common_side_effects, how_to_use, how_it_works,
            faq_for_medicine, medicine_interaction, product_use, image_file_name).
          </p>
        </div>

        {!file && (
          <label
            className={`file-dropzone${dragOver ? ' drag-over' : ''}`}
            style={{ margin: '0 0 16px', display: 'block' }}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files[0]); }}
          >
            <input
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={e => handleFile(e.target.files[0])}
            />
            <div style={{ fontSize: 36, marginBottom: 10 }}>📂</div>
            <div style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: 14, color: '#4f46e5', marginBottom: 6 }}>
              Click to choose the dataset CSV, or drag & drop here
            </div>
            <div className="t-meta">Large files supported (100s of MB) — this is a one-time bulk import, no row preview.</div>
          </label>
        )}

        {file && !importing && (
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
            <span className="t-meta">{file.name} · {(file.size / (1024 * 1024)).toFixed(1)} MB</span>
            <button className="t-link" style={{ marginLeft: 'auto', fontSize: 12 }} onClick={() => setFile(null)}>
              Change file
            </button>
          </div>
        )}

        {importing && (
          <div style={{ marginBottom: 16 }}>
            <div style={{
              height: 8, borderRadius: 9999, background: '#eef2ff', overflow: 'hidden',
              position: 'relative',
            }}>
              {progress < 100 ? (
                <div style={{
                  height: '100%', width: `${progress}%`, background: '#4f46e5',
                  transition: 'width .2s ease', borderRadius: 9999,
                }} />
              ) : (
                // Indeterminate "still working" animation for the server-side
                // insert phase, where we have no real progress number to show.
                <div className="indeterminate-bar" />
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              {progress >= 100 && <span className="spinner" aria-hidden="true" />}
              <span className="t-meta">
                {progress < 100
                  ? `Uploading… ${progress}%`
                  : `Uploaded — importing into the database… (${elapsed}s elapsed, usually 1–3 minutes for the full dataset)`}
              </span>
            </div>
            {progress >= 100 && (
              <div className="t-meta" style={{ marginTop: 4, color: '#9ca3af' }}>
                Please keep this tab open until it finishes.
              </div>
            )}
          </div>
        )}

        {importError && (
          <div className="error-state" style={{ marginBottom: 16 }}><span>⚠️</span> {importError}</div>
        )}

        <div className="modal-actions">
          <button onClick={onClose} className="btn-outline" disabled={importing} style={{ flex: 1, color: '#6b7280' }}>
            Cancel
          </button>
          <button
            className="btn-primary"
            style={{ flex: 2, opacity: (!file || importing) ? 0.6 : 1, cursor: (!file || importing) ? 'not-allowed' : 'pointer' }}
            disabled={!file || importing}
            onClick={handleImport}
          >
            {importing ? 'Importing…' : 'Import Dataset'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Product form modal (create / edit)
// ---------------------------------------------------------------------------
const ProductModal = ({ product, categories, onSave, onClose, token, tenantId, onImageChanged }) => {
  const isEdit = !!product?.id;
  const [form, setForm] = useState(
    isEdit
      ? {
          name: product.name || '',
          brand: product.brand || '',
          categoryId: product.categoryId || '',
          price: product.price?.toString() || '',
          mrp: product.mrp?.toString() || '',
          unit: product.unit || '',
          stockQuantity: product.stockQuantity?.toString() || '',
          prescription: product.prescription ?? false,
          emoji: product.emoji || '💊',
          tags: Array.isArray(product.tags) ? product.tags.join(', ') : (product.tags || ''),
          description: product.description || '',
          medicineType: product.medicineType || 'allopathy',
        }
      : EMPTY_FORM
  );
  const [imageUrl, setImageUrl] = useState(isEdit ? (product.imageUrl || null) : null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageError, setImageError] = useState('');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    if (!file.type.startsWith('image/')) { setImageError('Please choose an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { setImageError('Image must be under 5MB'); return; }
    setImageError('');
    setImageUploading(true);
    try {
      const updated = await uploadProductImage(token, tenantId, product.id, file);
      setImageUrl(updated.imageUrl || null);
      onImageChanged?.(product.id, updated.imageUrl || null);
    } catch (err) {
      setImageError(err.message || 'Upload failed');
    } finally {
      setImageUploading(false);
    }
  };

  const handleImageRemove = async () => {
    setImageError('');
    setImageUploading(true);
    try {
      await deleteProductImage(token, tenantId, product.id);
      setImageUrl(null);
      onImageChanged?.(product.id, null);
    } catch (err) {
      setImageError(err.message || 'Failed to remove image');
    } finally {
      setImageUploading(false);
    }
  };

  const set = (key) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [key]: val }));
    if (errors[key]) setErrors(e2 => ({ ...e2, [key]: null }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim())       errs.name = 'Name is required';
    if (!form.price || isNaN(parseFloat(form.price))) errs.price = 'Valid price required';
    if (!form.mrp   || isNaN(parseFloat(form.mrp)))   errs.mrp   = 'Valid MRP required';
    return errs;
  };

  const handleSubmit = async () => {
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    const payload = {
      ...form,
      price: parseFloat(form.price),
      mrp: parseFloat(form.mrp),
      stockQuantity: form.stockQuantity !== '' ? parseInt(form.stockQuantity, 10) : 0,
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
      categoryId: form.categoryId || null,
    };
    // Remove inStock — backend derives it from stockQuantity
    delete payload.inStock;
    await onSave(payload);
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 20, width: '100%', maxWidth: 560,
        maxHeight: '90vh', overflowY: 'auto', padding: '28px 28px 24px',
        boxShadow: '0 24px 64px rgba(79,70,229,0.18)',
        animation: 'fadeUp .25s ease both',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 22 }}>
          <div>
            <h2 className="modal-title">{isEdit ? 'Edit product' : 'Add product'}</h2>
            <p className="modal-sub">{isEdit ? 'Update inventory details' : 'Add a new item to your pharmacy inventory'}</p>
          </div>
          <button onClick={onClose} className="btn-close-x" style={{ fontSize: 22 }}>×</button>
        </div>

        {/* Form grid */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Emoji + Name row */}
          <div style={{ display: 'flex', gap: 10 }}>
            <div className="field" style={{ width: 72, flexShrink: 0, marginBottom: 0 }}>
              <label className="t-label">Emoji</label>
              <input
                value={form.emoji}
                onChange={set('emoji')}
                className="input"
                style={{ textAlign: 'center', fontSize: 20, padding: '8px 4px' }}
                maxLength={4}
              />
            </div>
            <div className="field" style={{ flex: 1, marginBottom: 0 }}>
              <label className="t-label">Product name <span className="required-star">*</span></label>
              <input
                value={form.name}
                onChange={set('name')}
                className={`input${errors.name ? ' err' : ''}`}
                placeholder="e.g. Paracetamol 500mg"
              />
              {errors.name && <div className="field-error">{errors.name}</div>}
            </div>
          </div>

          {/* Photo */}
          <div className="field" style={{ marginBottom: 0 }}>
            <label className="t-label">Medicine photo</label>
            {isEdit ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{
                  width: 64, height: 64, borderRadius: 12, flexShrink: 0,
                  background: '#f9fafb', border: '1px solid #e5e7eb',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  overflow: 'hidden',
                }}>
                  {imageUrl
                    ? <img src={imageUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <span style={{ fontSize: 24, opacity: 0.4 }}>📷</span>}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label className="btn-outline" style={{
                    fontSize: 12, padding: '6px 12px', cursor: imageUploading ? 'not-allowed' : 'pointer',
                    opacity: imageUploading ? 0.6 : 1, textAlign: 'center',
                  }}>
                    {imageUploading ? 'Uploading…' : imageUrl ? 'Replace photo' : 'Upload photo'}
                    <input type="file" accept="image/*" onChange={handleImageSelect} disabled={imageUploading} style={{ display: 'none' }} />
                  </label>
                  {imageUrl && (
                    <button
                      type="button"
                      onClick={handleImageRemove}
                      disabled={imageUploading}
                      style={{ fontSize: 11, color: '#dc2626', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0 }}
                    >
                      Remove photo
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 12, color: '#9ca3af', fontStyle: 'italic' }}>
                Save the product first — you can add a photo from the edit screen right after.
              </div>
            )}
            {imageError && <div className="field-error">{imageError}</div>}
          </div>

          {/* Brand + Unit */}
          <div className="grid-2col">
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="t-label">Brand</label>
              <input value={form.brand} onChange={set('brand')} className="input" placeholder="e.g. Sun Pharma" />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="t-label">Unit</label>
              <input value={form.unit} onChange={set('unit')} className="input" placeholder="e.g. Strip of 10" />
            </div>
          </div>

          {/* Stock quantity */}
          <div className="field" style={{ marginBottom: 0 }}>
            <label className="t-label">
              Stock quantity
              <span style={{ color: '#9ca3af', fontWeight: 400, marginLeft: 6 }}>(optional — set to 0 to mark out of stock)</span>
            </label>
            <input
              type="number" min="0" step="1"
              value={form.stockQuantity}
              onChange={set('stockQuantity')}
              className="input"
              placeholder="e.g. 100"
            />
          </div>

          {/* Price + MRP */}
          <div className="grid-2col">
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="t-label">
                Sellable price (₹) <span className="required-star">*</span>
                <span style={{ color: '#9ca3af', fontWeight: 400, marginLeft: 6 }}>(what the customer pays)</span>
              </label>
              <input
                type="number" step="0.01" min="0"
                value={form.price}
                onChange={set('price')}
                className={`input${errors.price ? ' err' : ''}`}
                placeholder="0.00"
              />
              {errors.price && <div className="field-error">{errors.price}</div>}
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="t-label">
                MRP (₹) <span className="required-star">*</span>
                <span style={{ color: '#9ca3af', fontWeight: 400, marginLeft: 6 }}>(pre-discount price)</span>
              </label>
              <input
                type="number" step="0.01" min="0"
                value={form.mrp}
                onChange={set('mrp')}
                className={`input${errors.mrp ? ' err' : ''}`}
                placeholder="0.00"
              />
              {errors.mrp && <div className="field-error">{errors.mrp}</div>}
            </div>
          </div>
          {/* Computed discount — MRP minus sellable price, shown so it's clear
              there's no separate "discount price" field to fill in. */}
          {!isNaN(parseFloat(form.price)) && !isNaN(parseFloat(form.mrp)) && parseFloat(form.mrp) > parseFloat(form.price) && (
            <div style={{ fontSize: 12, color: '#059669', marginTop: -8 }}>
              {Math.round((1 - parseFloat(form.price) / parseFloat(form.mrp)) * 100)}% off MRP
              (₹{(parseFloat(form.mrp) - parseFloat(form.price)).toFixed(2)} discount)
            </div>
          )}

          {/* Category + Medicine type */}
          <div className="grid-2col">
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="t-label">Category</label>
              <select value={form.categoryId} onChange={set('categoryId')} className="input" style={{ cursor: 'pointer' }}>
                <option value="">— None —</option>
                {categories.map(c => (
                  <option key={c.id} value={c.id}>{c.icon} {c.label}</option>
                ))}
              </select>
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="t-label">Medicine type</label>
              <select
                value={form.medicineType}
                onChange={set('medicineType')}
                className="input"
                style={{ cursor: 'pointer' }}
              >
                {MEDICINE_TYPES.map(m => (
                  <option key={m.value} value={m.value}>{m.icon} {m.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Description */}
          <div className="field" style={{ marginBottom: 0 }}>
            <label className="t-label">Description</label>
            <textarea
              value={form.description}
              onChange={set('description')}
              className="input"
              rows={2}
              placeholder="Brief description of the product…"
              style={{ resize: 'vertical', lineHeight: 1.5 }}
            />
          </div>

          {/* Tags */}
          <div className="field" style={{ marginBottom: 0 }}>
            <label className="t-label">Tags <span style={{ color: '#9ca3af', fontWeight: 400 }}>(comma-separated)</span></label>
            <input
              value={form.tags}
              onChange={set('tags')}
              className="input"
              placeholder="e.g. fever, pain, antibiotic"
            />
          </div>

          {/* Toggles — inStock removed, derived from stockQuantity by backend */}
          <div style={{ display: 'flex', gap: 24, paddingTop: 4 }}>
            {[
              { key: 'prescription', label: 'Prescription only', color: '#d97706', bg: '#fffbeb' },
            ].map(({ key, label, color, bg }) => (
              <label key={key} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                cursor: 'pointer', userSelect: 'none',
              }}>
                <div
                  onClick={() => setForm(f => ({ ...f, [key]: !f[key] }))}
                  style={{
                    width: 38, height: 22, borderRadius: 11,
                    background: form[key] ? color : '#d1d5db',
                    position: 'relative', transition: 'background .2s', cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  <div style={{
                    position: 'absolute', top: 3,
                    left: form[key] ? 18 : 3,
                    width: 16, height: 16, borderRadius: '50%',
                    background: '#fff', transition: 'left .2s',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  }} />
                </div>
                <span style={{
                  fontSize: 13, fontWeight: 500, fontFamily: 'DM Sans, sans-serif',
                  color: form[key] ? color : '#6b7280',
                }}>
                  {label}
                </span>
                {form[key] && (
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '1px 7px',
                    borderRadius: 9999, background: bg, color,
                    fontFamily: 'DM Sans, sans-serif',
                  }}>ON</span>
                )}
              </label>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="modal-actions" style={{ marginTop: 22 }}>
          <button onClick={onClose} className="btn-outline" style={{ flex: 1, color: '#6b7280' }}>
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="btn-primary"
            style={{ flex: 2, opacity: saving ? 0.7 : 1 }}
          >
            {saving ? 'Saving…' : isEdit ? 'Update product' : 'Add product'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Stock badge
// ---------------------------------------------------------------------------
const StockBadge = ({ inStock }) => (
  <span style={{
    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 9999,
    background: inStock ? '#ecfdf5' : '#fef2f2',
    color: inStock ? '#059669' : '#dc2626',
    border: `1px solid ${inStock ? '#6ee7b7' : '#fca5a5'}`,
    fontFamily: 'DM Sans, sans-serif',
    flexShrink: 0,
  }}>
    {inStock ? 'In stock' : 'Out of stock'}
  </span>
);

// ---------------------------------------------------------------------------
// Skeleton row
// ---------------------------------------------------------------------------
const SkeletonRow = () => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 14,
    padding: '14px 0', borderBottom: '1px solid #f3f4f6',
  }}>
    <div className="skeleton" style={{ width: 40, height: 40, borderRadius: 10 }} />
    <div style={{ flex: 1 }}>
      <div className="skeleton skeleton-line" style={{ width: '45%', height: 13, marginBottom: 6 }} />
      <div className="skeleton skeleton-line" style={{ width: '30%', height: 11 }} />
    </div>
    <div className="skeleton skeleton-line" style={{ width: 64, height: 13 }} />
    <div className="skeleton" style={{ width: 72, height: 22, borderRadius: 9999 }} />
    <div style={{ display: 'flex', gap: 8 }}>
      <div className="skeleton" style={{ width: 60, height: 30, borderRadius: 8 }} />
      <div className="skeleton" style={{ width: 60, height: 30, borderRadius: 8 }} />
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Summary stat card
// ---------------------------------------------------------------------------
// `onClick` is optional — when provided, the card becomes a filter CTA
// (used by "Prescription req" to jump straight to the Rx-only product list).
const StatCard = ({ icon, label, value, color, bg, onClick, active }) => (
  <div
    className="stat-card"
    onClick={onClick}
    role={onClick ? 'button' : undefined}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
    title={onClick ? (active ? 'Showing prescription-required products — click to clear' : 'Click to view prescription-required products') : undefined}
    style={{
      borderLeft: `3px solid ${color}`,
      cursor: onClick ? 'pointer' : 'default',
      outline: active ? `2px solid ${color}` : 'none',
      outlineOffset: -2,
      background: active ? bg : undefined,
    }}
  >
    <div style={{ fontSize: 22, marginBottom: 8 }}>{icon}</div>
    <div className="stat-value" style={{ color }}>{value}</div>
    <div className="stat-label">{label}{onClick && <span style={{ marginLeft: 6, fontSize: 10, opacity: 0.6 }}>{active ? '✕ clear' : '→ view'}</span>}</div>
  </div>
);

// ---------------------------------------------------------------------------
// Main: InventoryManagement
// ---------------------------------------------------------------------------
const InventoryManagement = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const { tenant } = useTenant();

  // ── Guard: ADMIN only ────────────────────────────────────────────────────
   useEffect(() => {
     if (!user) { navigate('/login', { replace: true }); return; }
     if (user.role !== 'ADMIN') { navigate('/unauthorized', { replace: true }); }
   }, [user, navigate]);

  // ── State ────────────────────────────────────────────────────────────────
  const [products,   setProducts]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [totalPages,    setTotalPages]    = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [stats, setStats] = useState({ total: 0, inStock: 0, outOfStock: 0, prescriptionRequired: 0 });

  // filters
  const [search,         setSearch]         = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterType,     setFilterType]     = useState('all');
  const [filterStock,    setFilterStock]    = useState('all');
  const [filterPrescription, setFilterPrescription] = useState(false); // CTA on the "Prescription req" stat card
  const [page,           setPage]           = useState(0);
  const searchDebounceRef = useRef(null);

  // modals
  const [editProduct,   setEditProduct]   = useState(null);   // product or {} for new
  const [deleteTarget,  setDeleteTarget]  = useState(null);
  const [deleting,      setDeleting]      = useState(false);
  const [togglingId,    setTogglingId]    = useState(null);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [showDatasetImport, setShowDatasetImport] = useState(false);

  // toast
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });

  const showToast = useCallback((message, type = 'success') => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast(t => ({ ...t, visible: false })), 2800);
  }, []);

  // Debounce the search box so we're not hitting the backend on every keystroke
  useEffect(() => {
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(searchDebounceRef.current);
  }, [search]);

  // Reset to page 0 whenever a filter changes
  useEffect(() => { setPage(0); }, [filterCategory, filterType, filterStock, filterPrescription, debouncedSearch]);

  // ── Load categories (once per tenant) ───────────────────────────────────
  useEffect(() => {
    if (!token || !tenant?.id) return;
    getInventoryCategories(token, tenant.id)
      .then(cats => setCategories(cats.sort((a, b) => a.displayOrder - b.displayOrder)))
      .catch(() => {});
  }, [token, tenant?.id]);

  // ── Load the current page of products — server-side filtered/paginated,
  // required at 124k-row scale (loading everything at once is what was
  // hanging the page). ───────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!token || !tenant?.id) return;
    setLoading(true);
    setError(null);
    try {
      const inStock = filterStock === 'all' ? undefined : filterStock === 'in';
      const { items, totalPages: tp, totalElements: te } = await getProductsPage(token, tenant.id, {
        categoryId: filterCategory,
        medicineType: filterType,
        q: debouncedSearch || undefined,
        inStock,
        prescription: filterPrescription ? true : undefined,
        page,
        size: 50,
      });
      setProducts(items);
      setTotalPages(tp);
      setTotalElements(te);
    } catch (e) {
      setError(`Failed to load inventory (${e.message})`);
    } finally {
      setLoading(false);
    }
  }, [token, tenant?.id, filterCategory, filterType, filterStock, filterPrescription, debouncedSearch, page]);

  useEffect(() => { load(); }, [load]);

  // ── Load dashboard stats separately — COUNT queries, not derived from the
  // (now paginated) product list. Refreshed alongside the product list. ───
  useEffect(() => {
    if (!token || !tenant?.id) return;
    getInventoryStats(token, tenant.id, { categoryId: filterCategory, medicineType: filterType })
      .then(setStats)
      .catch(() => {});
  }, [token, tenant?.id, filterCategory, filterType, products]);

  // `filtered` used to also apply search/stock filters client-side; those are
  // now applied server-side in load() above, so this page's `products` array
  // IS the filtered set already — kept as an alias so the render code below
  // (which references `filtered`) doesn't need to change.
  const filtered = products;


  // ── CRUD handlers ────────────────────────────────────────────────────────
  const handleSave = async (payload) => {
    try {
      if (editProduct?.id) {
        await updateProduct(token, tenant.id, editProduct.id, payload);
        showToast('Product updated');
      } else {
        await createProduct(token, tenant.id, payload);
        showToast('Product added');
      }
      setEditProduct(null);
      // Refresh from the server rather than splicing the local array — with
      // server-side pagination/sorting, a new or edited product may not
      // belong on the currently-displayed page at all.
      load();
    } catch (e) {
      showToast(`Save failed: ${e.message}`, 'error');
      throw e; // keeps modal open
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteProduct(token, tenant.id, deleteTarget.id);
      showToast('Product deleted');
      setDeleteTarget(null);
      load(); // refresh current page — same reasoning as handleSave above
    } catch (e) {
      showToast(`Delete failed: ${e.message}`, 'error');
    } finally {
      setDeleting(false);
    }
  };

  // FIX: when marking a product back in stock but stock_quantity is 0,
  // also send stockQuantity=1 to keep in_stock and stock_quantity in sync.
  const handleToggleStock = async (product) => {
    setTogglingId(product.id);
    try {
      const newInStock = !product.inStock;
      const stockQuantity = newInStock && (product.stockQuantity == null || product.stockQuantity === 0)
        ? 1
        : undefined;
      const updated = await toggleStock(token, tenant.id, product.id, newInStock, stockQuantity);
      setProducts(prev => prev.map(p =>
        p.id === product.id
          ? { ...p, inStock: updated.inStock, stockQuantity: updated.stockQuantity }
          : p
      ));
      showToast(product.inStock ? 'Marked out of stock' : 'Marked in stock');
    } catch (e) {
      showToast(`Stock update failed: ${e.message}`, 'error');
    } finally {
      setTogglingId(null);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────
  if (!user || user.role !== 'ADMIN') return null;

  const categoryMap = Object.fromEntries(categories.map(c => [c.id, c]));

  return (
    <div className="page">
      <main className="page-main">

        {/* Breadcrumb */}
        <div className="breadcrumb">
          <button className="t-link" onClick={() => navigate('/')}>Home</button>
          <span>›</span>
          <button className="t-link" onClick={() => navigate('/dashboard/super-admin')}>Dashboard</button>
          <span>›</span>
          <span className="bc-current">Inventory</span>
        </div>

        {/* Page header */}
        <div className="fade-up" style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: '1.5rem' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <h1 className="t-h1" style={{ marginBottom: 0 }}>📦 Inventory Management</h1>
              <span className="badge" style={{ background: '#eef2ff', border: '1px solid #c7d2fe', color: '#4f46e5' }}>
                ADMIN
              </span>
            </div>
            <p className="t-body-sm">Manage your pharmacy's product catalogue</p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              className="btn-outline btn-auto"
              onClick={() => exportProductsAsCSV(products)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
              title="Export current page only — this won't export the full 124k-row catalog"
            >
              📤 Export CSV
            </button>
            <button
              className="btn-outline btn-auto"
              onClick={() => setShowBulkImport(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
            >
              📥 Import CSV
            </button>
            <button
              className="btn-outline btn-auto"
              onClick={() => setShowDatasetImport(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}
              title="One-time import of the full ~124k-row pharmacy dataset"
            >
              🗂️ Import Full Dataset
            </button>
            <button
              className="btn-primary btn-auto"
              onClick={() => setEditProduct({})}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <span style={{ fontSize: 18, lineHeight: 1 }}>+</span>
              Add product
            </button>
          </div>
        </div>

        {/* Stat cards */}
        <div className="stat-grid fade-up-1" style={{ marginBottom: '1.5rem' }}>
          <StatCard icon="📦" label="Total products"  value={stats.total}                color="#4f46e5" bg="#eef2ff" />
          <StatCard icon="✅" label="In stock"         value={stats.inStock}              color="#059669" bg="#ecfdf5" />
          <StatCard icon="❌" label="Out of stock"     value={stats.outOfStock}           color="#dc2626" bg="#fef2f2" />
          <StatCard
            icon="℞"
            label="Prescription req"
            value={stats.prescriptionRequired}
            color="#d97706"
            bg="#fffbeb"
            active={filterPrescription}
            onClick={() => setFilterPrescription(v => !v)}
          />
        </div>

        {/* Filters row */}
        <div className="card fade-up-2" style={{ marginBottom: '1.25rem', padding: '14px 18px' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            {/* Search */}
            <div className="search-wrap" style={{ flex: '1 1 200px', minWidth: 180, marginBottom: 0 }}>
              <svg className="search-icon" width="16" height="16" fill="none" stroke="#9ca3af" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35" strokeLinecap="round"/>
              </svg>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="search-input"
                placeholder="Search by name or brand…"
                style={{ height: 36, fontSize: 13 }}
              />
              {search && (
                <button className="search-clear" onClick={() => setSearch('')} style={{ fontSize: 16 }}>×</button>
              )}
            </div>

            {/* Category filter */}
            <select
              value={filterCategory}
              onChange={e => setFilterCategory(e.target.value)}
              className="select-filter"
            >
              <option value="all">All categories</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.icon} {c.label}</option>
              ))}
            </select>

            {/* Medicine type filter */}
            <select
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
              className="select-filter"
            >
              <option value="all">All types</option>
              {MEDICINE_TYPES.map(m => (
                <option key={m.value} value={m.value}>{m.icon} {m.label}</option>
              ))}
            </select>

            {/* Stock filter */}
            <div className="toggle-group">
              {[
                { key: 'all', label: 'All' },
                { key: 'in',  label: '✅ In stock' },
                { key: 'out', label: '❌ Out of stock' },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setFilterStock(key)}
                  className={`toggle-item${filterStock === key ? ' active' : ''}`}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* Results count */}
            {!loading && (
              <span className="t-meta" style={{ marginLeft: 'auto' }}>
                {totalElements.toLocaleString()} product{totalElements !== 1 ? 's' : ''}
                {totalPages > 1 ? ` · page ${page + 1} of ${totalPages}` : ''}
              </span>
            )}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="strip-warn" style={{ marginBottom: '1rem' }}>
            <span>⚠️</span>
            <span style={{ fontSize: 13, color: '#92400e' }}>{error}</span>
            <button className="t-link" style={{ marginLeft: 'auto' }} onClick={load}>Retry</button>
          </div>
        )}

        {/* Table / list */}
        <div className="card fade-up-3" style={{ padding: 0, overflowX: 'auto', overflowY: 'hidden' }}>

          {/* Table header — hidden on mobile, shown on desktop */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr 100px 110px 130px',
            gap: 0, padding: '10px 20px',
            background: '#f8f9ff', borderBottom: '1px solid #f3f4f6',
            fontSize: 11, fontWeight: 600, color: '#9ca3af',
            textTransform: 'uppercase', letterSpacing: '0.06em',
            fontFamily: 'DM Sans, sans-serif',
          }} className="inv-table-header">
            <span>Product</span>
            <span>Category</span>
            <span>Type</span>
            <span style={{ textAlign: 'right' }}>Price</span>
            <span style={{ textAlign: 'center' }}>Status</span>
            <span style={{ textAlign: 'right' }}>Actions</span>
          </div>

          {/* Rows */}
          <div style={{ padding: '0 20px' }} className="inv-rows-wrap">
            {loading ? (
              Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
            ) : filtered.length === 0 ? (
              <div className="empty-state" style={{ padding: '48px 0' }}>
                <div className="empty-icon">📦</div>
                <p className="empty-title" style={{ fontSize: 16 }}>No products found</p>
                <p className="empty-body">
                  {search || filterCategory !== 'all' || filterType !== 'all' || filterStock !== 'all'
                    ? 'Try adjusting your filters.'
                    : 'Add your first product to get started.'}
                </p>
                {!search && filterCategory === 'all' && filterType === 'all' && filterStock === 'all' && (
                  <button className="btn-primary btn-auto" onClick={() => setEditProduct({})}>
                    + Add first product
                  </button>
                )}
              </div>
            ) : (
              filtered.map((product, idx) => {
                const cat = categoryMap[product.categoryId];
                const typeObj = MEDICINE_TYPES.find(m => m.value === product.medicineType);
                const isToggling = togglingId === product.id;
                const discount = product.mrp && product.price
                  ? Math.round((1 - product.price / product.mrp) * 100)
                  : 0;

                return (
                  <div
                    key={product.id}
                    className="inv-row"
                    onClick={() => navigate(`/services/pharmacy/${product.id}`)}
                    style={{
                      borderBottom: idx < filtered.length - 1 ? '1px solid #f3f4f6' : 'none',
                      animation: `fadeUp .3s ${idx * 20}ms ease both`,
                      opacity: isToggling ? 0.6 : 1,
                      transition: 'opacity .2s',
                      cursor: 'pointer',
                    }}
                  >
                    {/* Product name + brand */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }} className="inv-col-product">
                      <div style={{
                        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                        background: 'linear-gradient(135deg, #eef2ff, #f5f3ff)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 20,
                      }}>
                        {product.emoji || '💊'}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{
                          fontSize: 13.5, fontWeight: 600, color: '#1e1b4b',
                          fontFamily: 'Sora, system-ui, sans-serif',
                          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                        }}>
                          {product.name}
                          {product.prescription && (
                            <span className="badge-rx" style={{ marginLeft: 6 }}>℞</span>
                          )}
                        </div>
                        <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 2, fontFamily: 'DM Sans, sans-serif' }}>
                          {product.brand || '—'}{product.unit ? ` · ${product.unit}` : ''}
                        </div>
                        {/* Category + type shown inline on mobile only */}
                        <div className="inv-mobile-meta">
                          <span>{cat ? `${cat.icon} ${cat.label}` : '—'}</span>
                          <span style={{ color: '#d1d5db' }}>·</span>
                          <span>{typeObj ? `${typeObj.icon} ${typeObj.label}` : product.medicineType || '—'}</span>
                        </div>
                      </div>
                    </div>

                    {/* Category — desktop only */}
                    <div style={{ fontSize: 12.5, color: '#6b7280', fontFamily: 'DM Sans, sans-serif' }} className="inv-col-desktop">
                      {cat ? `${cat.icon} ${cat.label}` : '—'}
                    </div>

                    {/* Medicine type — desktop only */}
                    <div style={{ fontSize: 12.5, color: '#6b7280', fontFamily: 'DM Sans, sans-serif' }} className="inv-col-desktop">
                      {typeObj ? `${typeObj.icon} ${typeObj.label}` : product.medicineType || '—'}
                    </div>

                    {/* Price */}
                    <div style={{ textAlign: 'right' }} className="inv-col-price">
                      <div className="t-price" style={{ fontSize: 13.5 }}>₹{parseFloat(product.price).toFixed(2)}</div>
                      {discount > 0 && (
                        <div style={{ fontSize: 10.5, color: '#059669', fontWeight: 600, fontFamily: 'DM Sans, sans-serif' }}>
                          {discount}% off
                        </div>
                      )}
                    </div>

                    {/* Stock status */}
                    <div style={{ textAlign: 'center' }} className="inv-col-status">
                      <StockBadge inStock={product.inStock} />
                      {product.stockQuantity != null && (
                        <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3, fontFamily: 'DM Sans, sans-serif' }}>
                          {product.stockQuantity} units
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }} className="inv-col-actions">
                      {/* Toggle stock */}
                      <button
                        disabled={isToggling}
                        onClick={(e) => { e.stopPropagation(); handleToggleStock(product); }}
                        title={product.inStock ? 'Mark out of stock' : 'Mark in stock'}
                        style={{
                          padding: '6px 10px', borderRadius: 8, fontSize: 12, fontWeight: 600,
                          border: `1px solid ${product.inStock ? '#fca5a5' : '#6ee7b7'}`,
                          background: product.inStock ? '#fef2f2' : '#ecfdf5',
                          color: product.inStock ? '#dc2626' : '#059669',
                          cursor: isToggling ? 'not-allowed' : 'pointer',
                          fontFamily: 'DM Sans, sans-serif',
                          transition: 'all .15s', opacity: isToggling ? 0.5 : 1,
                        }}
                      >
                        {isToggling ? '…' : product.inStock ? '❌' : '✅'}
                      </button>

                      {/* Edit */}
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditProduct(product); }}
                        className="btn-primary-sm"
                        style={{ padding: '6px 12px', fontSize: 12 }}
                      >
                        Edit
                      </button>

                      {/* Delete */}
                      <button
                        onClick={(e) => { e.stopPropagation(); setDeleteTarget(product); }}
                        style={{
                          padding: '6px 10px', borderRadius: 8, fontSize: 12,
                          border: '1px solid #fecaca', background: 'none',
                          color: '#dc2626', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif',
                          transition: 'background .15s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                        onMouseLeave={e => e.currentTarget.style.background = 'none'}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Footer: pagination */}
        {!loading && products.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
            <div style={{ textAlign: 'center', marginTop: 8 }}>
              <span className="t-meta">{totalElements.toLocaleString()} total</span>
            </div>
          </div>
        )}
      </main>

      {/* Modals */}
      {editProduct !== null && (
        <ProductModal
          product={editProduct?.id ? editProduct : null}
          categories={categories}
          onSave={handleSave}
          onClose={() => setEditProduct(null)}
          token={token}
          tenantId={tenant?.id}
          onImageChanged={() => load()}
        />
      )}

      {deleteTarget && (
        <ConfirmModal
          product={deleteTarget}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
          deleting={deleting}
        />
      )}

      {showBulkImport && (
        <BulkImportModal
          token={token}
          tenantId={tenant?.id}
          onImport={(count) => {
            setShowBulkImport(false);
            showToast(`✅ Imported ${count} products successfully`);
            load(); // refresh the list
          }}
          onClose={() => setShowBulkImport(false)}
        />
      )}

      {showDatasetImport && (
        <DatasetImportModal
          token={token}
          tenantId={tenant?.id}
          onImport={(count) => {
            setShowDatasetImport(false);
            showToast(`✅ Imported ${count} products from the dataset`);
            load(); // refresh the list
          }}
          onClose={() => setShowDatasetImport(false)}
        />
      )}

      <Toast {...toast} />
    </div>
  );
};
//---------------------------------------------------------------------------
export default InventoryManagement;